import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { authService } from '../services/auth';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import LandingNavbar from '../components/ui/LandingNavbar';
import BrandScroller from '../components/ui/BrandScroller';

const inputCls = "bg-gray-100/80 border-transparent text-gray-900 placeholder:text-gray-400 rounded-full h-14 px-6 text-base font-medium focus-visible:ring-0 focus-visible:ring-offset-0";

const ResetPassword = () => {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    username: '', token: '', password: '', password_confirmation: '',
  });

  useEffect(() => {
    const token    = searchParams.get('token');
    const username = searchParams.get('username');
    if (token && username) {
      setFormData(prev => ({ ...prev, token, username: decodeURIComponent(username) }));
    } else {
      toast({ title: 'Enlace inválido', description: 'El enlace no es válido.', variant: 'destructive' });
      navigate('/forgot-password');
    }
  }, [searchParams, navigate]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.password || formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }
    if (formData.password !== formData.password_confirmation) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.resetPassword(formData);
      if (response.success) {
        setSuccess(true);
        toast({ title: '¡Contraseña restablecida!', description: 'Ya puedes iniciar sesión.' });
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err) {
      const msg = err.message || 'Error al restablecer. Verifica que el enlace no haya expirado.';
      setError(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <LandingNavbar onBack={() => navigate('/login')} />
      <BrandScroller />

      <div className="flex-1 px-6 py-12 flex flex-col items-center">
        <div className="w-full max-w-md space-y-8">

          {success ? (
            <div className="pt-4 space-y-6">
              <h1 className="font-semibold text-black tracking-tight"
                style={{ fontSize: 'clamp(28px,6vw,44px)', letterSpacing: '-0.025em', lineHeight: 1.06 }}>
                ¡Listo!
              </h1>
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                <p className="text-green-700 text-sm text-center">
                  Contraseña restablecida. Redirigiendo al inicio de sesión…
                </p>
              </div>
              <Button onClick={() => navigate('/login')}
                className="w-full bg-black text-white rounded-full h-14 text-base font-semibold hover:opacity-90 active:scale-95 transition-all">
                Ir al inicio de sesión
              </Button>
            </div>
          ) : (
            <>
              <div className="pt-4">
                <h1 className="font-semibold text-black tracking-tight"
                  style={{ fontSize: 'clamp(28px,6vw,44px)', letterSpacing: '-0.025em', lineHeight: 1.06 }}>
                  Nueva<br />contraseña
                </h1>
                <p className="text-gray-400 text-[15px] mt-3">Mínimo 8 caracteres</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <Input name="password" type="password" value={formData.password}
                  onChange={handleChange} placeholder="Nueva contraseña"
                  className={inputCls} required minLength={8} autoFocus />
                <Input name="password_confirmation" type="password"
                  value={formData.password_confirmation} onChange={handleChange}
                  placeholder="Confirmar contraseña" className={inputCls} required minLength={8} />

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-3">
                    <p className="text-red-500 text-sm text-center">{error}</p>
                  </div>
                )}

                <Button type="submit" disabled={loading || !formData.token || !formData.username}
                  className="w-full bg-black text-white rounded-full h-14 text-base font-semibold hover:opacity-90 active:scale-95 transition-all disabled:opacity-50">
                  {loading ? 'Restableciendo...' : 'Restablecer contraseña'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default ResetPassword;
